import type { SchemaResponse, FieldSchema, SchemaType } from 'Listrak.Customer.Segmentation.Api.Client';
import type { SchemaPresentation } from '../../definitions/schema-mapping/types';
import type { SchemaDropdownFields, SchemaDropdownCriteria, FlatSchemaListItem } from './types';

export function FormattedSchemaData(
  s: SchemaResponse,
  flatFieldsMap: Map<string, FieldSchema>,
  schemaPresentation: Readonly<Array<SchemaPresentation>>,
): Array<SchemaDropdownCriteria> {
  return RemoveUnusedSchemaDropdownGroups(
    ConstructSchemaDropdownData(s, flatFieldsMap, schemaPresentation),
  );
}

export function ConstructSchemaDropdownData(
  s: SchemaResponse,
  flatFieldsMap: Map<string, FieldSchema>,
  schemaPresentation: Readonly<Array<SchemaPresentation>>,
): Array<SchemaDropdownCriteria> {
  const mapGroup = (g: Array<SchemaPresentation>): Array<SchemaDropdownCriteria> => ConstructSchemaDropdownData(s, flatFieldsMap, g);
  const mapFields = (f: Array<string>, childFieldUids: Array<string>): Array<SchemaDropdownFields> => f.reduce((acc: Array<SchemaDropdownFields>, uid: string) => {
    const field = flatFieldsMap.get(uid);

    if (field) {
      childFieldUids.push(field.fieldUid);
      acc.push({
        title: field.name,
        value: field.fieldUid,
      })
    }
    return acc;
  }, []);
  const mapSchemaType = (type: SchemaType, parent: Partial<SchemaPresentation>): Array<SchemaPresentation> => {
    const src = s.data.find(({ schemaType }) => schemaType === type)?.fieldGroups || [];
    const sortedSrc = src.length > 0 ? src.sort((a, b) => a.fieldGroupName.localeCompare(b.fieldGroupName)) : [];

    return sortedSrc.map((fg) => ({
      title: fg.fieldGroupName,
      iconClass: parent.iconClass,
      iconColor: parent.iconColor,
      fields: fg.fields.map((f) => f.fieldUid),
    }));
  }

  return schemaPresentation.map((group) => {
    let { groups, fields, schemaType, ...rest } = group;
    const childFieldUids: Array<string> = [];

    groups = schemaType ? mapSchemaType(schemaType, { ...rest }) : groups;

    const g = mapGroup(groups || []);
    const f = mapFields(fields || [], childFieldUids);

    return {
      ...rest,
      ...g.length > 0 && { groups: g },
      ...f.length > 0 && { fields: f.sort((a, b) => a.title.localeCompare(b.title)) },
      childFieldUids: g.length > 0 ? childFieldUids.concat(g.map((group) => group.childFieldUids).flat()) : childFieldUids,
    };
  }, []);
}

export function RemoveUnusedSchemaDropdownGroups(
  data: Array<SchemaDropdownCriteria>,
): Array<SchemaDropdownCriteria> {
  return data.reduce((acc, curv) => {
    if (curv.childFieldUids.length > 0) {
      acc.push({
        ...curv,
        ...curv.groups && { groups: RemoveUnusedSchemaDropdownGroups(curv.groups) },
      });
    }
    return acc;
  }, []);
}

export function FlatFormattedSchemaData(data: Array<SchemaDropdownCriteria>, list: Array<FlatSchemaListItem> = [], int: number = 0): Array<FlatSchemaListItem> {
  const _acc = structuredClone(list);

  return data.reduce((acc: Array<FlatSchemaListItem>, item: SchemaDropdownCriteria) => {
    const { groups, fields, ...rest } = item;

    acc.push({
      ...rest,
      ...int === 0 && { isCategory: true },
      ...int === 1 && { isGroup: true },
      ...int === 2 && { isSubGroup: true },
    });

    if (groups) { acc = FlatFormattedSchemaData(groups, acc, int + 1) }
    if (fields) { fields.forEach((f) => acc.push(f)) }

    return acc;
  }, _acc);
}

export function FieldsMap(s: SchemaResponse): Map<string, FieldSchema> {
  const map = new Map<string, FieldSchema>();

  s.data.forEach((schema) => {
    schema.fieldGroups.forEach((group) => {
      group.fields.forEach((field) => map.set(field.fieldUid, field))
    })
  });

  return map;
}

export function ModifiersMap(s: SchemaResponse): Map<string, FieldSchema> {
  const map = new Map<string, FieldSchema>();
  const m = s?.modifiers || {};

  Object.entries(m).forEach(([ key, value ]) => map.set(key, value));
  return map;
}

export function OperatorGroups(s: SchemaResponse): { [key: string]: Array<string> } {
  return s.operatorGroups;
}
